import type { ResourceFieldType, ResourceAggregation, ResourceField } from '../types';

/**
 * Compute an aggregated value from an array of numbers.
 * Returns undefined for empty arrays or 'none' aggregation.
 */
export function computeAggregate(aggregate: ResourceAggregation, values: number[]): number | undefined {
  if (values.length === 0 || aggregate === 'none') return undefined;
  switch (aggregate) {
    case 'sum': return values.reduce((a, b) => a + b, 0);
    case 'avg': return values.reduce((a, b) => a + b, 0) / values.length;
    case 'min': return Math.min(...values);
    case 'max': return Math.max(...values);
    default: return undefined;
  }
}

/**
 * Format a ResourceField value for display on badges and sidebar items.
 */
export function formatFieldValue(field: ResourceField): string {
  return formatFieldValueRaw(field.type, field.value, field.unit, field.interval);
}

/**
 * Format an aggregated field value for display.
 * Uses toLocaleString with fraction digits for aggregated numbers.
 */
export function formatAggregatedValue(field: {
  type: ResourceFieldType;
  value: number;
  unit?: string;
  interval?: string;
}): string {
  if (field.type === 'currency') {
    return `${field.unit || '\u20ac'}${field.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${formatInterval(field.interval)}`;
  }
  if (field.type === 'percentage') return `${field.value.toFixed(1)}%`;
  if (field.type === 'duration') return `${field.value}${field.unit || 'ms'}`;
  if (field.type === 'number') return `${field.value}${field.unit ? ` ${field.unit}` : ''}`;
  return String(field.value);
}

function formatFieldValueRaw(
  type: ResourceFieldType,
  value: string | number,
  unit?: string,
  interval?: string,
): string {
  if (type === 'currency') {
    return `${unit || '\u20ac'}${typeof value === 'number' ? value.toLocaleString() : value}${formatInterval(interval)}`;
  }
  if (type === 'percentage') return `${value}%`;
  if (type === 'duration') return `${value}${unit || 'ms'}`;
  if (type === 'number') return `${value}${unit ? ` ${unit}` : ''}`;
  return String(value);
}

function formatInterval(interval?: string): string {
  if (!interval) return '';
  if (interval === 'monthly') return '/mo';
  if (interval === 'yearly') return '/yr';
  return '/hr';
}
